/**
 * The zoning rule book's display vocabulary - permissions, status notes, and use-case names.
 *
 * ⚠️ ONE COPY, DELIBERATELY. This started out inside `ZoningRules.tsx`. When the property page's
 * panel needed the same labels (ZS2), copying them would have recreated the exact silent drift
 * that `use_case` and `status_note` are fixed vocabularies to prevent - two screens quietly
 * disagreeing about what `condition_may_bar` means, with nothing to fail. The server serves the
 * VALUES (see `api.getZoningRules`); this file is the single place they are given English.
 *
 * ⭐ Each status-note label says what to DO, not just what is wrong. That is the entire point of
 * categorising instead of writing prose: `code_ambiguous` is not a warning, it is an instruction
 * to get a written interpretation from Planning.
 */
export type Permission = 'by_right' | 'cup' | 'prohibited' | 'unknown';

/**
 * WHY a rule cannot be relied on, or the caveat on one that can.
 *
 * ⚠️ Shown on rules that DO have an answer as well as on unknowns. The highest-risk row in the
 * table is not an unknown - it is a by-right permission with a development standard that may gut
 * it, because that is the one that gets taken to an owner.
 */
export type StatusNote =
  | 'not_enumerated'
  | 'document_unavailable'
  | 'designation_repealed'
  | 'code_ambiguous'
  | 'jurisdiction_unclear'
  | 'condition_may_bar';

/** Each label says what to DO, not just what is wrong - that is the point of categorising. */
export const STATUS_NOTE_LABEL: Record<StatusNote, string> = {
  not_enumerated: 'Use not in the code — needs a Planning determination',
  document_unavailable: 'Governing document not public — needs a records request',
  designation_repealed: 'Zone no longer exists — pull the current zoning',
  code_ambiguous: 'Code contradicts itself — needs a written interpretation',
  jurisdiction_unclear: 'Governing jurisdiction unresolved — confirm by APN',
  condition_may_bar: 'Permitted, but a development standard may bar it',
};

export const STATUS_NOTE_SHORT: Record<StatusNote, string> = {
  not_enumerated: 'Not in code',
  document_unavailable: 'Doc unavailable',
  designation_repealed: 'Zone repealed',
  code_ambiguous: 'Code conflict',
  jurisdiction_unclear: 'Jurisdiction?',
  condition_may_bar: '⚠ Standard may bar',
};

export const STATUS_NOTE_STYLE: Record<StatusNote, string> = {
  not_enumerated: 'bg-slate-50 text-slate-700 border-slate-300',
  document_unavailable: 'bg-slate-50 text-slate-700 border-slate-300',
  designation_repealed: 'bg-primary-100 text-primary-500 border-primary-200',
  code_ambiguous: 'bg-fuchsia-50 text-fuchsia-800 border-fuchsia-300',
  jurisdiction_unclear: 'bg-info-wash text-info border-info/30',
  // The loudest, on purpose: a permission that may not survive its own conditions.
  condition_may_bar: 'bg-red-100 text-red-900 border-red-400 font-semibold',
};

export const PERMISSION_LABEL: Record<Permission, string> = {
  by_right: 'Permitted by right',
  cup: 'CUP required',
  prohibited: 'Prohibited',
  unknown: 'Researched, unclear',
};

export const PERMISSION_STYLE: Record<Permission, string> = {
  by_right: 'bg-success-wash text-success border-success/30',
  cup: 'bg-warning-wash text-warning border-warning/30',
  prohibited: 'bg-red-50 text-red-800 border-red-200',
  unknown: 'bg-gray-50 text-gray-700 border-gray-200',
};

/**
 * ⭐ A FIXED VOCABULARY DESERVES FIXED DISPLAY NAMES. The generic title-caser turns
 * `rv_boat_storage` into "Rv Boat Storage" and `ev_charging_depot` into "Ev Charging Depot".
 * Flagged as cosmetic in ZS2; ZS3 put the same strings on a CLIENT BROCHURE, where scruffy
 * becomes careless. The map is the display half of the vocabulary the server already fixes.
 */
const USE_LABEL: Record<string, string> = {
  truck_terminal: 'Truck Terminal',
  outdoor_storage: 'Outdoor Storage',
  container_storage: 'Container Storage',
  contractor_yard: 'Contractor Yard',
  rv_boat_storage: 'RV / Boat Storage',
  self_storage: 'Self Storage',
  ev_charging_depot: 'EV Charging Depot',
  // FZ (the 140th): the industrial and retail building uses.
  warehouse_distribution: 'Warehouse / Distribution',
  warehouse_large: 'Large Warehouse',
  manufacturing_light: 'Light Manufacturing',
  manufacturing_heavy: 'Heavy Manufacturing',
  retail_store: 'Retail Store',
  restaurant: 'Restaurant',
  drive_through: 'Drive-Through',
  gas_station: 'Gas Station',
  car_wash: 'Car Wash',
};

export const prettyUse = (u: string) =>
  USE_LABEL[u] ?? u.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

